import React from 'react';

import {assert} from 'Asserts';
import {getRegistrySupportedOligoAwareTypes, TagSchema} from 'constants/RegistryConstants';
import {BatchSummary} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import BioentitySearchInput from 'inventory/BioentitySearchInput';
import BatchSearchInput from 'samples/batches/BatchSearchInput';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {SearchContext} from 'search/SearchConstants';
import {FilterKey, SingleValueFilter} from 'search/SearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import APIUtil from 'util/APIUtil';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import FilterFormSkeletonNodeLayout from './base/FilterFormSkeletonNodeLayout';
import PrimaryFilterFormSkeleton from './base/PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './base/SecondaryFilterFormSkeleton';

export enum ParentTypeOption {
  BIOENTITY_SCHEMA = 'Entity Schema',
  BIOENTITY = 'Entity',
  BATCH = 'Batch',
}

function _valueToParentType(value: string | null): ParentTypeOption | null {
  if (!value) {
    return null;
  }
  if (APIUtil.isBiologicalEntity(value)) {
    return ParentTypeOption.BIOENTITY;
  }
  if (APIUtil.isBatch(value)) {
    return ParentTypeOption.BATCH;
  }
  assert(APIUtil.isTagSchema(value), `Unexpected API ID: ${value}`);
  return ParentTypeOption.BIOENTITY_SCHEMA;
}

interface ContainerParentFilter extends SingleValueFilter {
  key: FilterKey.CONTAINER_PARENT;
}
interface Props extends BaseFilterFormProps {
  filter: ContainerParentFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: ContainerParentFilter) => void;
  onRemove?: () => void;
}
interface State {
  desiredParentType: ParentTypeOption;
}
export default class ContainerParentFilterForm extends React.Component<Props, State> {
  state: State = {
    desiredParentType: _valueToParentType(this.props.filter.value) || ParentTypeOption.BIOENTITY_SCHEMA,
  };

  handleChangeParentType = (parentType: ParentTypeOption): void => {
    this.setState({desiredParentType: parentType});
    this.props.onChangeFilter({...this.props.filter, value: null});
  };

  handleParentBioentitySchemaChange = (tagSchema: TagSchema | null): void => {
    assert(tagSchema != null, 'TagSchemaSelector returned a null tagSchema');
    this.props.onChangeFilter({...this.props.filter, value: tagSchema.id});
  };

  handleParentBioentityChange = (bioentities: Array<BioentitySummary>): void => {
    const parentBioentity = bioentities.length ? bioentities[0] : null;
    this.props.onChangeFilter({...this.props.filter, value: parentBioentity ? parentBioentity.id : null});
  };

  handleParentBatchChange = (batches: Array<BatchSummary>): void => {
    const parentBatch = batches.length ? batches[0] : null;
    this.props.onChangeFilter({...this.props.filter, value: parentBatch ? parentBatch.id : null});
  };

  getParentTypeForRender(): ParentTypeOption {
    // Because `desiredParentType` lives in component state, and the filter value lives in higher-up state,
    // these two pieces of state can get out-of-sync (e.g. via `handleChangeParentType`). To avoid rendering
    // issues (BNCH-6763), desiredParentType should be treated as a best-effort value.
    return _valueToParentType(this.props.filter.value) || this.state.desiredParentType;
  }

  inputForParentType(): JSX.Element {
    const selectedItems = this.props.filter.value ? [{id: this.props.filter.value}] : [];
    const parentType = this.getParentTypeForRender();
    switch (parentType) {
      case ParentTypeOption.BIOENTITY:
        return (
          <BioentitySearchInput
            onChange={this.handleParentBioentityChange}
            selectedItems={selectedItems}
            context={SearchContext.LISTING}
          />
        );
      case ParentTypeOption.BATCH:
        return (
          <BatchSearchInput
            onChange={this.handleParentBatchChange}
            selectedBatches={selectedItems}
            context={SearchContext.LISTING}
          />
        );
      default:
        assert(parentType === ParentTypeOption.BIOENTITY_SCHEMA, `Unexpected parentType: ${parentType}`);
        return (
          <div className="u-flex-grow">
            <TagSchemaSelector
              oligoAwareFolderItemTypes={getRegistrySupportedOligoAwareTypes()}
              onChange={this.handleParentBioentitySchemaChange}
              selectedTagSchemaId={this.props.filter.value}
            />
          </div>
        );
    }
  }

  render(): JSX.Element {
    const typeDropdown = (
      <Dropdown
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            isMulti={false}
            width={Menu.WIDTHS.FULL}
            value={this.getParentTypeForRender()}
          >
            {Object.values(ParentTypeOption).map((option) => (
              <Option key={option} onSelect={this.handleChangeParentType} value={option}>
                {option}
              </Option>
            ))}
          </Menu>
        }
      >
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          fullWidth={true}
          isDropdown={true}
          size={ButtonSize.SMALL}
          text={this.getParentTypeForRender()}
        />
      </Dropdown>
    );
    const input = this.inputForParentType();
    const skeletonProps = pickDefined(this.props, ...BASE_FILTER_FORM_PROP_NAMES);
    if (this.props.filterStyle === FilterStyle.PRIMARY) {
      return (
        <PrimaryFilterFormSkeleton
          {...skeletonProps}
          buttonSize={this.props.buttonSize}
          content={<FilterFormSkeletonNodeLayout nodeGrid={[[typeDropdown], [input]]} />}
        />
      );
    } else {
      if (!this.props.onRemove) {
        throw new Error('Expected props.onRemove to be present when filterStyle is SECONDARY');
      }
      return (
        <SecondaryFilterFormSkeleton
          {...skeletonProps}
          nodeGrid={[[typeDropdown], [input]]}
          onChangeFilterKey={this.props.onChangeFilterKey}
          selectedFilterKey={this.props.selectedFilterKey}
          onRemove={this.props.onRemove}
        />
      );
    }
  }
}
